/**
 * Charm Viewer JavaScript
 * Handles interactive charm selection and media switching
 */

(function () {
  'use strict';

  class CharmViewer {
    constructor(container) {
      this.container = container;
      this.mediaWrapper = container.querySelector('.charm-viewer__media-wrapper');
      this.charmItems = container.querySelectorAll('.charm-item');
      this.activeCharmId = null;
      this.charmsSwiper = null;
      this.modelViewerScriptLoaded = false;
      this.boundHandleCharmClick = this.handleCharmClick.bind(this);

      this.init();
    }

    init() {
      if (!this.container || this.charmItems.length === 0) return;

      this.bindEvents();
      this.initSwiper();
      this.setInitialActiveCharm();
    }

    setInitialActiveCharm() {
      const firstCharm = this.charmItems[0];
      if (firstCharm) {
        this.setActiveCharm(firstCharm.dataset.charmId);
        this.updateMedia(firstCharm);
        this.updateActiveState(firstCharm);
      }
    }

    bindEvents() {
      this.charmItems.forEach((item) => {
        const button = item.querySelector('.charm-item__button');
        if (button) {
          button.addEventListener('click', () => this.boundHandleCharmClick(item));
        }
      });
    }

    initSwiper() {
      const charmsContainer = this.container.querySelector('.charm-viewer__charms');
      if (!charmsContainer) return;

      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        // Listen for Swiper to become available
        this.waitForSwiper(() => this.initSwiper());
        return;
      }

      // Check if Swiper is already initialized on this container
      if (charmsContainer.swiper) {
        this.charmsSwiper = charmsContainer.swiper;
        return;
      }

      try {
        this.charmsSwiper = new Swiper(charmsContainer, {
          slidesPerView: 'auto',
          freeMode: true,
          grabCursor: true,
          slidesOffsetAfter: 12,
          slidesOffsetBefore: 12,
          spaceBetween: 12,
          breakpoints: {
            750: {
              spaceBetween: 0,
              slidesOffsetAfter: 0,
              slidesOffsetBefore: 0,
            },
          },
        });
      } catch (error) {
        console.warn('Failed to initialize Swiper:', error);
      }
    }

    waitForSwiper(callback) {
      // If Swiper is already available, call callback immediately
      if (typeof Swiper !== 'undefined') {
        callback();
        return;
      }

      // Create a custom event to signal when Swiper is ready
      const swiperReadyEvent = new CustomEvent('swiperReady');

      // Listen for the custom event
      const handleSwiperReady = () => {
        document.removeEventListener('swiperReady', handleSwiperReady);
        callback();
      };

      document.addEventListener('swiperReady', handleSwiperReady);

      // Also listen for window load as a fallback
      const handleWindowLoad = () => {
        window.removeEventListener('load', handleWindowLoad);
        if (typeof Swiper !== 'undefined') {
          callback();
        }
      };

      window.addEventListener('load', handleWindowLoad);
    }

    handleCharmClick(charmItem) {
      const charmId = charmItem.dataset.charmId;
      if (charmId === this.activeCharmId) return;

      this.setActiveCharm(charmId);
      this.updateMedia(charmItem);
      this.updateActiveState(charmItem);
    }

    setActiveCharm(charmId) {
      this.activeCharmId = charmId;
    }

    updateMedia(charmItem) {
      if (!this.mediaWrapper) return;

      const showType = charmItem.dataset.showType;
      const video = charmItem.dataset.video;
      const modelUrl = charmItem.dataset.modelUrl;
      const title = charmItem.dataset.title || '';
      const description = charmItem.dataset.description || '';

      let mediaHTML = '';

      if (showType === 'video' && video) {
        mediaHTML = `<div class="charm-viewer__video-container">${video}</div>`;
      } else if (showType === 'model' && modelUrl) {
        mediaHTML = `
          <div class="charm-viewer__model-container">
            <product-model class="charm-viewer__model">
              <model-viewer
                src="${modelUrl}"
                alt="3D Model"
                camera-controls
                auto-rotate
                class="charm-viewer__model-viewer"
                camera-orbit="0deg 75deg 150%"
                min-camera-orbit="auto auto 100%"
                max-camera-orbit="auto auto 300%"
                loading="eager"
                reveal="auto"
                ar
                ar-modes="webxr scene-viewer quick-look">
              </model-viewer>
            </product-model>
          </div>
        `;
      } else {
        mediaHTML = `
          <div class="charm-viewer__placeholder">
            <p>No media available</p>
          </div>
        `;
      }

      mediaHTML += `
        <div class="charm-viewer__floating-info">
          <div class="charm-viewer__floating-title">${title}</div>
          <div class="charm-viewer__floating-description">${description}</div>
        </div>
      `;

      this.mediaWrapper.innerHTML = mediaHTML;
      this.mediaWrapper.dataset.charmId = charmItem.dataset.charmId;

      if (showType === 'model' && modelUrl) {
        this.initModelViewer();
      }
    }

    updateActiveState(activeItem) {
      this.charmItems.forEach((item) => {
        item.classList.remove('charm-item--active');
      });
      activeItem.classList.add('charm-item--active');
    }

    initModelViewer() {
      const modelViewer = this.mediaWrapper.querySelector('model-viewer');
      if (!modelViewer) return;

      if (window.Shopify?.ModelViewerUI) {
        try {
          new window.Shopify.ModelViewerUI(modelViewer);
        } catch (error) {
          console.warn('Model viewer initialization failed:', error);
        }
      } else if (!this.modelViewerScriptLoaded) {
        this.loadModelViewerScript();
      }
    }

    loadModelViewerScript() {
      if (document.querySelector('script[src*="model-viewer"]')) {
        this.modelViewerScriptLoaded = true;
        return;
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://cdn.shopify.com/s/files/1/0678/7324/3447/files/model-viewer.min.js';
      script.onload = () => {
        this.modelViewerScriptLoaded = true;
        // Re-initialize model viewer after script loads
        this.initModelViewer();
      };
      script.onerror = () => {
        console.warn('Failed to load model viewer script');
      };
      document.head.appendChild(script);
    }

    destroy() {
      if (this.charmsSwiper) {
        this.charmsSwiper.destroy();
      }
      this.charmItems.forEach((item) => {
        const button = item.querySelector('.charm-item__button');
        if (button) {
          button.removeEventListener('click', this.boundHandleCharmClick);
        }
      });
    }
  }

  // Initialize charm viewers when DOM is loaded
  function initCharmViewers() {
    const charmViewers = document.querySelectorAll('.charm-viewer');
    charmViewers.forEach((viewer) => {
      new CharmViewer(viewer);
    });
  }

  // Wait for DOM to be ready and check for Swiper
  function initWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initWhenReady);
      return;
    }

    // If Swiper is already available, initialize immediately
    if (typeof Swiper !== 'undefined') {
      initCharmViewers();
      return;
    }

    // Listen for Swiper to become available
    const handleSwiperReady = () => {
      document.removeEventListener('swiperReady', handleSwiperReady);
      initCharmViewers();
    };

    document.addEventListener('swiperReady', handleSwiperReady);

    // Also listen for window load as a fallback
    const handleWindowLoad = () => {
      window.removeEventListener('load', handleWindowLoad);
      if (typeof Swiper !== 'undefined') {
        initCharmViewers();
      }
    };

    window.addEventListener('load', handleWindowLoad);
  }

  // Start the initialization process
  initWhenReady();

  // Export for potential external use
  window.CharmViewer = CharmViewer;
})();
